import { API_BASE } from './apiBase';
import { todayKey, currentStreak, getProfile } from './profileStore';

// Notifications via Web Push: streak reminders (the server nudges at 7 PM
// local, only if you played yesterday but not today) and duel results (someone
// played your run or challenged you back). One subscription covers both.

export const REMIND_HOUR = 19;
const STATE_CACHE = 'bf-state';
const STATE_URL = '/__bf/state';
const DISMISS_KEY = 'bf_reminder_dismissed_at';
const DISMISS_DAYS = 14;

export type ReminderStatus = 'unsupported' | 'denied' | 'off' | 'on';

const post = (path: string, body: unknown) =>
  fetch(`${API_BASE}/api/push/${path}`, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(body) });

// The service worker only exists in production builds (and iOS only allows
// push inside the installed home-screen app), so check for a live registration.
async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

export async function reminderStatus(): Promise<ReminderStatus> {
  const reg = await registration();
  if (!reg) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return (await reg.pushManager.getSubscription()) ? 'on' : 'off';
}

export function reminderRecentlyDismissed(): boolean {
  try {
    return Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

export function dismissReminderPrompt() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
}

// Lets the service worker put the streak count in the notification text.
async function saveStateForServiceWorker(streak: number, lastDailyDate: string | null) {
  try {
    const cache = await caches.open(STATE_CACHE);
    await cache.put(STATE_URL, new Response(JSON.stringify({ streak, lastDailyDate })));
  } catch { /* best effort */ }
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const b64 = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

// Duel alerts are addressed by player id, so a subscription must carry one.
function playerId(): string {
  let id = localStorage.getItem('bf_player_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('bf_player_id', id);
  }
  return id;
}

// Re-registers an existing subscription so it's linked to this player (older
// ones may predate the player id). No prompt: only runs if already allowed.
export async function refreshSubscription(): Promise<void> {
  if ((await reminderStatus()) === 'on' && Notification.permission === 'granted') await enableReminders();
}

export async function enableReminders(): Promise<ReminderStatus> {
  const reg = await registration();
  if (!reg) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const keyRes = await fetch(`${API_BASE}/api/push/public-key`);
  if (!keyRes.ok) return 'unsupported';
  const { publicKey } = await keyRes.json() as { publicKey: string };

  const sub = (await reg.pushManager.getSubscription())
    ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) });

  const profile = getProfile();
  const streak = currentStreak();
  const res = await post('subscribe', {
    subscription: sub.toJSON(),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    remindHour: REMIND_HOUR,
    playerId: playerId(),
    lastDailyDate: profile.streak.lastDate,
    streak,
  });
  if (!res.ok) {
    await sub.unsubscribe();
    return 'off';
  }
  await saveStateForServiceWorker(streak, profile.streak.lastDate);
  return 'on';
}

export async function disableReminders(): Promise<void> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await post('unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe();
}

// Called when a Daily is finished, so tonight's reminder is skipped.
export async function reportDailyProgress(streak: number, date = todayKey()): Promise<void> {
  await saveStateForServiceWorker(streak, date);
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await post('progress', { endpoint: sub.endpoint, lastDailyDate: date, streak }).catch(() => {});
}

// A real notification right after opting in, so players see it works.
export async function sendTestNotification(kind: 'streak' | 'duel' = 'streak'): Promise<void> {
  const reg = await registration();
  if (kind === 'duel') {
    await reg?.showNotification('🔔 Notifications are on', {
      body: "We'll ping you when someone takes on your score or a topic you asked for goes live.",
      icon: '/icon-192.png',
      tag: 'reminder-confirm',
    });
    return;
  }
  await reg?.showNotification('🔔 Streak reminders are on', {
    body: `We'll ping you at ${REMIND_HOUR % 12 || 12} PM, only if your streak is at risk.`,
    icon: '/icon-192.png',
    tag: 'reminder-confirm',
  });
}
